import { CloudUpload } from "@mui/icons-material";
import {
  Button,
  ButtonProps,
  CircularProgress,
  Box,
  Typography,
  styled,
  Alert,
} from "@mui/material";
import { IUploaderProps } from "features/media/types";
import { useState, useCallback, useRef, useEffect } from "react";
import { motion, AnimatePresence, easeInOut, Variants } from "motion/react";
import { Uploader, UploaderRef } from "features/media";

interface IUploaderButtonProps extends IUploaderProps {
  text: string;
  uploading: boolean;
  buttonProps?: ButtonProps;
  useDropZone?: boolean;
  dropZoneText?: string;
  previewUrl?: string;
  previewFileName?: string;
}

// Styled components
const StyledDropZone = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isDragOver",
})<{ isDragOver: boolean }>(({ theme, isDragOver }) => ({
  border: `2px dashed ${isDragOver ? theme.palette.primary.vibrant : theme.palette.primary.light}`,
  borderRadius: theme.shape.borderRadius * 2,
  background: isDragOver
    ? `${theme.palette.primary.vibrant}08`
    : theme.palette.background.paper,
  minHeight: 140,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  cursor: "pointer",
  padding: theme.spacing(3),
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    background: isDragOver
      ? `${theme.palette.primary.vibrant}12`
      : `${theme.palette.primary.main}04`,
    borderColor: theme.palette.primary.vibrant,
    transform: "translateY(-1px)",
  },
}));

const StyledDropOverlay = styled(motion.div)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `${theme.palette.primary.vibrant}15`,
  borderRadius: theme.shape.borderRadius * 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1,
  backdropFilter: "blur(2px)",
}));

const StyledDropContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1.5),
  textAlign: "center",
}));

const StyledGradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.vibrant})`,
  color: "white",
  fontWeight: 600,
  fontSize: "1.1rem",
  padding: theme.spacing(1.5, 3),
  boxShadow: `0 4px 15px ${theme.palette.primary.main}4D`,
  borderRadius: theme.shape.borderRadius * 1.5,
  "&:hover": {
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.vibrant})`,
    boxShadow: `0 6px 20px ${theme.palette.primary.main}66`,
    transform: "translateY(-2px)",
    transition: "all 0.2s ease-in-out",
  },
  "&:disabled": {
    background: `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
    opacity: 0.7,
    transform: "none",
  },
  transition: "all 0.2s ease-in-out",
}));

function UploaderButton({
  uploading,
  text,
  buttonProps,
  useDropZone = false,
  dropZoneText = "Drag and drop your file here or click to browse",
  previewUrl,
  previewFileName,
  ...rest
}: IUploaderButtonProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const uploaderRef = useRef<UploaderRef>(null);

  // Animation variants for drop zone
  const dropZoneVariants = {
    initial: { scale: 1 },
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.2,
        ease: easeInOut,
      },
    },
    tap: {
      scale: 0.95,
      transition: {
        duration: 0.1,
      },
    },
    pulse: {
      scale: [1, 1.03, 1],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: easeInOut,
      },
    },
  } as Variants;

  // File type validation helper
  const isFileTypeAllowed = useCallback(
    (file: File): boolean => {
      if (!rest.accept) return true;

      const acceptTypes = rest.accept.split(",").map((type) => type.trim());
      return acceptTypes.some((type) => {
        if (type.startsWith(".")) {
          // File extension check
          return file.name.toLowerCase().endsWith(type.toLowerCase());
        }
        // MIME type: support wildcard e.g. image/* matches image/png, image/jpeg
        if (type.endsWith("/*")) {
          const prefix = type.slice(0, -1); // "image/" from "image/*"
          return file.type.startsWith(prefix);
        }
        return file.type === type;
      });
    },
    [rest.accept],
  );

  // Get allowed file types for error message
  const getAllowedFileTypes = useCallback((): string => {
    if (!rest.accept) return "any file type";

    const acceptTypes = rest.accept.split(",").map((type) => type.trim());
    return acceptTypes
      .map((type) => (type.startsWith(".") ? type.toUpperCase() : type))
      .join(", ");
  }, [rest.accept]);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((prev) => prev - 1);
      if (dragCounter === 0) {
        setIsDragOver(false);
      }
    },
    [dragCounter],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setDragCounter(0);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);

        const invalidFiles = files.filter((file) => !isFileTypeAllowed(file));
        if (invalidFiles.length > 0) {
          const fileNames = invalidFiles.map((f) => f.name).join(", ");
          const allowedTypes = getAllowedFileTypes();
          setError(
            `Invalid file type(s): ${fileNames}. Please upload: ${allowedTypes}`,
          );
          return;
        }

        const file = files[0];
        if (rest.onProgress) {
          rest.onProgress(0, "starting", file);
        }

        try {
          await uploaderRef.current?.uploadFile(file);
          if (rest.onProgress) {
            rest.onProgress(100, "finished", file);
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Upload failed";
          setError(errorMessage);
          if (rest.onError) {
            rest.onError(errorMessage);
          }
        }
      }
    },
    [rest, isFileTypeAllowed, getAllowedFileTypes],
  );

  const handlePaste = useCallback(
    async (e: React.ClipboardEvent) => {
      // Only handle paste when there is an image in the clipboard
      const items = e.clipboardData?.items;
      if (!items || items.length === 0) {
        return;
      }

      const imageItem = Array.from(items).find((item) =>
        item.type.startsWith("image/"),
      );
      if (!imageItem) {
        return;
      }

      e.preventDefault();

      const file = imageItem.getAsFile();
      if (!file) {
        return;
      }

      if (!isFileTypeAllowed(file)) {
        const allowedTypes = getAllowedFileTypes();
        setError(`Invalid file type. Please upload: ${allowedTypes}`);
        return;
      }

      if (rest.onProgress) {
        rest.onProgress(0, "starting", file);
      }

      try {
        await uploaderRef.current?.uploadFile(file);
        if (rest.onProgress) {
          rest.onProgress(100, "finished", file);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        setError(errorMessage);
        if (rest.onError) {
          rest.onError(errorMessage);
        }
      }
    },
    [getAllowedFileTypes, isFileTypeAllowed, rest],
  );

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  // Document-level paste so Cmd+V / Ctrl+V works even when focus is elsewhere (e.g. after clicking the label)
  const pasteHandlerRef = useRef(handlePaste);
  pasteHandlerRef.current = handlePaste;
  useEffect(() => {
    if (!useDropZone) return;
    const onDocPaste = (e: ClipboardEvent) => {
      if (e.defaultPrevented) return;
      const items = e.clipboardData?.items;
      if (!items?.length) return;
      const hasImage = Array.from(items).some((item) =>
        item.type.startsWith("image/"),
      );
      if (!hasImage) return;
      e.preventDefault();
      const synthetic = e as unknown as React.ClipboardEvent;
      pasteHandlerRef.current(synthetic);
    };
    document.addEventListener("paste", onDocPaste, true);
    return () => document.removeEventListener("paste", onDocPaste, true);
  }, [useDropZone]);

  if (useDropZone) {
    return (
      <>
        <Box
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onPaste={handlePaste}
          tabIndex={0}
          sx={{ width: "100%", outline: "none" }}
          role="button"
          aria-label="Upload file by dropping, pasting (Cmd+V or Ctrl+V), or click to browse"
        >
          <Box
            component="label"
            sx={{ display: "block", width: "100%", cursor: "pointer" }}
          >
            <motion.div
              variants={dropZoneVariants}
              initial="initial"
              whileHover="hover"
              whileTap="tap"
              animate={uploading ? "pulse" : "initial"}
              style={{ width: "100%" }}
            >
              <StyledDropZone isDragOver={isDragOver}>
                <AnimatePresence>
                  {isDragOver && (
                    <StyledDropOverlay
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                    >
                      <Typography variant="h6" color="primary" fontWeight={600}>
                        Drop your file here!
                      </Typography>
                    </StyledDropOverlay>
                  )}
                </AnimatePresence>

                <StyledDropContent>
                  {uploading ? (
                    <CircularProgress size={28} color="primary" />
                  ) : previewUrl ? (
                    <>
                      <Box
                        component="img"
                        src={previewUrl}
                        alt="Uploaded preview"
                        sx={{
                          maxHeight: 120,
                          maxWidth: "100%",
                          objectFit: "contain",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        {previewFileName || "Image uploaded"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Drop or paste to replace
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CloudUpload
                        sx={{ fontSize: 36, color: "primary.main" }}
                      />
                      <Typography
                        variant="body1"
                        color="text.primary"
                        fontWeight={500}
                      >
                        {dropZoneText}
                      </Typography>
                    </>
                  )}
                </StyledDropContent>
              </StyledDropZone>
              <Uploader ref={uploaderRef} {...rest} />
            </motion.div>
          </Box>
        </Box>

        {error && error.length > 0 && (
          <Alert
            onClose={handleCloseError}
            severity="error"
            sx={{ width: "100%", mt: 2 }}
          >
            {error}
          </Alert>
        )}
      </>
    );
  }

  return (
    <>
      <StyledGradientButton
        color="primary"
        variant="contained"
        component="label"
        disabled={uploading}
        {...buttonProps}
        startIcon={
          uploading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <CloudUpload />
          )
        }
      >
        {text}
        <Uploader ref={uploaderRef} {...rest} />
      </StyledGradientButton>
    </>
  );
}

export default UploaderButton;
